import { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Pagination from '../../components/common/Pagination';
import { MY_TRASH_QUERY } from '../../graphql/queries';
import { DELETE_TRASH_ITEM_NOW_MUTATION } from '../../graphql/mutations';
import { countdown, formatBytes, removalText } from '../../utils/trash';
import styles from './Trash.module.css';

interface TrashItem {
  id: string;
  kind: 'file' | 'project';
  name: string;
  fileType: string | null;
  projectId: string;
  projectName: string;
  bytes: number;
  fileCount: number;
  deletedAt: string;
  deletedBy: string | null;
  removesAt: string | null;
  removing: boolean;
}

const TYPE_ICONS: Record<string, string> = {
  two_d: 'architecture',
  three_d: 'view_in_ar',
  point_cloud: 'grain',
  document: 'description',
  gaussian: 'blur_on',
  orthomosaic: 'satellite_alt',
  video360: 'panorama_photosphere',
};

const PAGE_SIZE = 20;

const formatDate = (s: string) => new Date(s).toLocaleString('en-US', {
  month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
});

const Trash = () => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [now, setNow] = useState(() => Date.now());
  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  const { data, loading, error, refetch } = useQuery(MY_TRASH_QUERY, {
    variables: { page, limit: PAGE_SIZE, search: search || undefined },
    fetchPolicy: 'cache-and-network',
  });

  const [deleteNow] = useMutation(DELETE_TRASH_ITEM_NOW_MUTATION, {
    onCompleted: (d) => { setBusyId(null); setNotice(d.deleteTrashItemNow.message); refetch(); },
    onError: (e) => { setBusyId(null); setActionError(e.message); refetch(); },
  });

  const trash = data?.myTrash;
  const items: TrashItem[] = trash?.items ?? [];
  const total: number = trash?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  useEffect(() => { if (data && page > totalPages) setPage(totalPages); }, [data, page, totalPages]);

  const handleDeleteNow = (item: TrashItem) => {
    const what = item.kind === 'project'
      ? `the project "${item.name}" and its ${item.fileCount} file${item.fileCount === 1 ? '' : 's'}`
      : `"${item.name}"`;
    if (!window.confirm(`Delete ${what} for good? This cannot be undone.`)) return;
    setActionError('');
    setNotice('');
    setBusyId(item.id);
    deleteNow({ variables: { kind: item.kind, id: item.id } });
  };

  const limitGb: number = trash?.immediateDeleteGbPerMonth ?? 0;

  return (
    <DashboardLayout onSearch={(q) => { setSearch(q); setPage(1); }} searchPlaceholder="Search Trash...">
      <div className="space-y-6">
        <div className={styles.header}>
          <div>
            <h2 className="text-3xl font-bold tracking-tight text-on-surface font-headline">Trash</h2>
            <p className="text-on-surface-variant mt-1">
              Files and projects deleted from your account. They count on your storage until they are removed for good.
            </p>
          </div>
          {trash && (
            <div className={styles.totalBadge}>
              <span className="material-symbols-outlined text-base">delete</span>
              {total} item{total === 1 ? '' : 's'} · {formatBytes(trash.totalBytes)} on your storage
            </div>
          )}
        </div>

        {trash && (
          <div className={styles.policy}>
            <span className="material-symbols-outlined">schedule</span>
            <div>
              {trash.retentionDays > 0 ? (
                <p>
                  Items are removed for good <strong>{trash.retentionDays} day{trash.retentionDays === 1 ? '' : 's'}</strong> after
                  they were deleted, and stop counting on your storage then.
                  {trash.nextRemovalAt && (
                    <> Next removal: <strong>{Date.parse(trash.nextRemovalAt) > now ? `in ${countdown(Date.parse(trash.nextRemovalAt) - now)}` : 'at the next removal run'}</strong>.</>
                  )}
                </p>
              ) : (
                <p>Items stay here until automatic removal is turned on for the platform.</p>
              )}
              {trash.allowImmediateDelete && (
                <p className={styles.policyNote}>
                  You can also delete items for good now
                  {limitGb > 0
                    ? <> — up to {limitGb} GB a month ({trash.immediateDeleteUsedGb.toFixed(2)} GB used, {(trash.immediateDeleteLeftGb ?? 0).toFixed(2)} GB left this month).</>
                    : '.'}
                </p>
              )}
              <p className={styles.policyNote}>Deleted something by mistake? Ask an administrator to restore it before it is removed.</p>
            </div>
          </div>
        )}

        {actionError && <p className={styles.errorBanner} role="alert">{actionError}</p>}
        {notice && <p className={styles.noticeBanner} role="status">{notice}</p>}

        <div className="bg-white rounded-xl overflow-hidden">
          {loading && items.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-on-surface-variant">
              <span className="material-symbols-outlined animate-spin mr-2">refresh</span>
              Loading Trash...
            </div>
          ) : error && items.length === 0 ? (
            <div className={styles.loadError} role="alert">
              <span className="material-symbols-outlined">error</span>
              Could not load Trash: {error.message}
            </div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center">
              <span className="material-symbols-outlined text-4xl text-outline mb-3 block">delete_outline</span>
              <p className="text-on-surface-variant">{search ? 'Nothing in Trash matches your search' : 'Trash is empty'}</p>
            </div>
          ) : (
            <>
              <div className={styles.tableWrap}>
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-surface-container-low">
                      <th className="table-header">Item</th>
                      <th className="table-header">Size</th>
                      <th className="table-header">Deleted</th>
                      <th className="table-header">Removed for good</th>
                      {trash?.allowImmediateDelete && <th className="table-header text-right">Actions</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-outline-variant/10">
                    {items.map((item) => (
                      <tr key={`${item.kind}-${item.id}`} className="table-row">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <span className="material-symbols-outlined text-base text-on-surface-variant">
                              {item.kind === 'project' ? 'folder' : TYPE_ICONS[item.fileType ?? ''] ?? 'insert_drive_file'}
                            </span>
                            <div className="min-w-0">
                              <p className={styles.name} title={item.name}>{item.name}</p>
                              <p className={styles.sub}>
                                {item.kind === 'project'
                                  ? `Project · ${item.fileCount} file${item.fileCount === 1 ? '' : 's'}`
                                  : item.projectName}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-xs text-on-surface-variant">{formatBytes(item.bytes)}</td>
                        <td className="px-6 py-4 text-xs text-on-surface-variant">
                          {formatDate(item.deletedAt)}
                          {item.deletedBy && <span className={styles.sub}>by {item.deletedBy}</span>}
                        </td>
                        <td className="px-6 py-4 text-xs" title={item.removesAt ? formatDate(item.removesAt) : undefined}>
                          <span className={item.removing ? styles.removing : styles.countdown}>
                            {removalText(item.removesAt, item.removing, now)}
                          </span>
                        </td>
                        {trash?.allowImmediateDelete && (
                          <td className="px-6 py-4 text-right">
                            <button
                              className={styles.deleteBtn}
                              onClick={() => handleDeleteNow(item)}
                              disabled={item.removing || busyId === item.id}
                            >
                              <span className={`material-symbols-outlined text-base ${busyId === item.id ? 'animate-spin' : ''}`}>
                                {busyId === item.id ? 'refresh' : 'delete_forever'}
                              </span>
                              {busyId === item.id ? 'Deleting…' : 'Delete now'}
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} noun="items" />
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Trash;
